import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, Monitor, Smartphone, Sparkles } from "lucide-react";
import { DeviceFrame } from "@/ui";
import { cn } from "@/lib/cn";
import { TreeRenderer } from "@/editor/TreeRenderer";
import type { ComponentTree } from "@/editor/types";
import { ThemeStyleInjector, type ThemeTokens } from "@/editor/theme/ThemeStyleInjector";

type DeviceView = "desktop" | "mobile";

/**
 * The LIVE preview canvas — renders the real working component tree via the
 * editor's TreeRenderer (read-only: no onSelect) inside the same browser-chrome
 * / device-frame shell the simulated canvas uses, so live and offline modes feel
 * identical. Updates whenever the working tree changes (after Apply).
 */
export function LivePreview({
  tree,
  loading,
  error,
  device,
  onDeviceChange,
  domain,
  themeTokens,
  selectedNodeId,
  onSelect,
  onEditText,
}: {
  tree: ComponentTree | null;
  loading: boolean;
  /** Set when loading the page tree failed — shown instead of the empty state. */
  error?: string | null;
  device: DeviceView;
  onDeviceChange: (d: DeviceView) => void;
  domain: string;
  /** Active design tokens — injected as `--dl-*` scoped to the canvas only. */
  themeTokens?: ThemeTokens | null;
  /** Currently-selected node id (click-to-select). */
  selectedNodeId?: string | null;
  /** Selection callback; omit for a plain read-only preview. */
  onSelect?: (id: string) => void;
  /** Inline text edit: commit a text prop edited directly on the canvas. */
  onEditText?: (nodeId: string, prop: string, value: string) => void;
}) {
  const scopeRef = useRef<HTMLDivElement>(null);
  const [scope, setScope] = useState<HTMLDivElement | null>(null);
  useEffect(() => setScope(scopeRef.current), [tree]);

  // Inline (WYSIWYG) text editing: for the SELECTED node, make its text elements
  // (tagged data-dl-edit="<prop>" in the components) contentEditable, and commit
  // the new text on blur/Enter via setProp. Re-attaches after each re-render
  // (tree dep) since React replaces the edited DOM node. Escape reverts.
  useEffect(() => {
    if (!scope || !selectedNodeId || !onEditText) return;
    const host = scope.querySelector<HTMLElement>(`[data-node-id="${CSS.escape(selectedNodeId)}"]`);
    if (!host) return;
    const els = Array.from(host.querySelectorAll<HTMLElement>("[data-dl-edit]"));
    const cleanups = els.map((el) => {
      const prop = el.getAttribute("data-dl-edit") ?? "";
      const original = el.textContent ?? "";
      el.setAttribute("contenteditable", "true");
      el.setAttribute("spellcheck", "false");
      el.dataset.dlEditing = "1";
      const commit = () => {
        const v = el.textContent ?? "";
        if (prop && v !== original) onEditText(selectedNodeId, prop, v);
      };
      const onKey = (ev: KeyboardEvent) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
          ev.preventDefault();
          el.blur();
        } else if (ev.key === "Escape") {
          el.textContent = original;
          el.blur();
        }
      };
      el.addEventListener("blur", commit);
      el.addEventListener("keydown", onKey);
      return () => {
        el.removeAttribute("contenteditable");
        el.removeAttribute("spellcheck");
        delete el.dataset.dlEditing;
        el.removeEventListener("blur", commit);
        el.removeEventListener("keydown", onKey);
      };
    });
    return () => cleanups.forEach((c) => c());
  }, [scope, selectedNodeId, onEditText, tree]);

  const page = tree ? (
    <div ref={scopeRef} className="bg-canvas">
      <ThemeStyleInjector tokens={themeTokens ?? null} scope={scope} />
      {/* Inline-edit affordance: editable text shows a text caret + soft ring. */}
      <style>{`
        [data-dl-editing]{cursor:text;border-radius:3px;outline:none;transition:box-shadow .15s}
        [data-dl-editing]:hover{box-shadow:0 0 0 2px color-mix(in srgb,var(--dl-accent,#c0392b) 22%,transparent)}
        [data-dl-editing]:focus{box-shadow:0 0 0 2px var(--dl-accent,#c0392b)}
      `}</style>
      <TreeRenderer
        node={tree.root}
        selectedNodeId={selectedNodeId ?? null}
        onSelect={onSelect}
        breakpoint={device === "mobile" ? "mobile" : "base"}
      />
    </div>
  ) : null;

  return (
    <div className="rounded-card border border-hairline bg-paper-2 p-3 dark:bg-white/5">
      {/* canvas toolbar */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="eyebrow">Live preview</span>
          <span className="inline-flex items-center gap-1 rounded-chip bg-paper px-2 py-0.5 font-mono text-[10px] text-accent shadow-sm dark:bg-white/10 dark:shadow-none">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            real page
          </span>
        </div>
        <div className="inline-flex rounded-full border border-hairline bg-paper p-0.5 dark:bg-white/5">
          {([
            { v: "desktop", Icon: Monitor },
            { v: "mobile", Icon: Smartphone },
          ] as const).map(({ v, Icon }) => (
            <button
              key={v}
              onClick={() => onDeviceChange(v)}
              aria-label={`Preview ${v}`}
              className={cn(
                "grid h-7 w-8 place-items-center rounded-full transition-colors",
                device === v
                  ? "bg-paper-2 text-ink-strong dark:bg-white/10"
                  : "text-muted hover:text-ink",
              )}
            >
              <Icon className="h-[15px] w-[15px]" strokeWidth={1.5} />
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <LoadingCanvas />
      ) : error && !tree ? (
        <ErrorCanvas message={error} />
      ) : !tree ? (
        <EmptyCanvas />
      ) : device === "mobile" ? (
        <div className="py-2">
          <DeviceFrame>
            <div className="h-full overflow-y-auto pt-5">{page}</div>
          </DeviceFrame>
        </div>
      ) : (
        <div className="overflow-hidden rounded-card border border-hairline bg-canvas shadow-sm dark:shadow-none">
          {/* browser chrome */}
          <div className="flex items-center gap-2 border-b border-hairline bg-paper px-3 py-2">
            <span className="flex gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-hairline" />
              <span className="h-2.5 w-2.5 rounded-full bg-hairline" />
              <span className="h-2.5 w-2.5 rounded-full bg-hairline" />
            </span>
            <div className="ml-1 flex-1 truncate rounded-chip bg-paper-2 px-2.5 py-1 text-center font-mono text-[10px] text-subtle dark:bg-white/5">
              {domain}
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">{page}</div>
        </div>
      )}
    </div>
  );
}

function LoadingCanvas() {
  return (
    <div className="grid place-items-center rounded-card border border-dashed border-hairline bg-canvas px-6 py-16 text-center">
      <Loader2 className="h-7 w-7 animate-spin text-subtle" strokeWidth={1.5} />
      <p className="mt-3 text-[14px] font-medium text-ink-strong">Loading your page…</p>
      <p className="mt-1 max-w-[34ch] text-[13px] leading-relaxed text-muted">
        Fetching the current version's component tree.
      </p>
    </div>
  );
}

function EmptyCanvas() {
  return (
    <div className="grid place-items-center rounded-card border border-dashed border-hairline bg-canvas px-6 py-16 text-center">
      <Sparkles className="h-7 w-7 text-subtle" strokeWidth={1.25} />
      <p className="mt-3 text-[14px] font-medium text-ink-strong">No page tree yet</p>
      <p className="mt-1 max-w-[34ch] text-[13px] leading-relaxed text-muted">
        This page has no published version to edit. Ask Nara to draft something to
        begin.
      </p>
    </div>
  );
}

function ErrorCanvas({ message }: { message: string }) {
  return (
    <div className="grid place-items-center rounded-card border border-dashed border-accent/40 bg-accent-soft/40 px-6 py-16 text-center">
      <AlertTriangle className="h-7 w-7 text-accent" strokeWidth={1.5} />
      <p className="mt-3 text-[14px] font-medium text-ink-strong">Couldn't load this page</p>
      <p className="mt-1 max-w-[40ch] text-[13px] leading-relaxed text-muted">{message}</p>
      <p className="mt-1 max-w-[40ch] text-[12px] leading-relaxed text-subtle">
        Switch pages and back, or refresh to retry.
      </p>
    </div>
  );
}
